import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useClinicTimeZone } from './clinicClock';
import type { Direction } from './direction';
import { useLanguage } from './LanguageProvider';
import { localeForLanguage, type SupportedLanguage } from './languages';
import { loadMessages, type Messages } from './messages';

type TranslationVars = Record<string, string | number | null | undefined>;

export type I18n = {
  language: SupportedLanguage;
  locale: string;
  direction: Direction;
  isRTL: boolean;
  t: (key: keyof Messages | (string & {}), vars?: TranslationVars) => string;
  formatDate: (value?: string | Date | null) => string;
  formatDateTime: (value?: string | Date | null) => string;
  formatTime: (value?: string | Date | null) => string;
  /** Long form: "Tuesday, 3 March 2026". */
  formatLongDate: (value?: string | Date | null) => string;
  /** Month heading: "March 2026". */
  formatMonth: (value?: string | Date | null) => string;
  /** Weekday + day + month, no year: "Tue 3 Mar". */
  formatShortDate: (value?: string | Date | null) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  /**
   * Money in the currency it was billed in. Never converted: an amount the
   * clinic billed renders in that currency, full stop.
   */
  formatCurrency: (value: number | null | undefined, currency: string) => string;
  /** `CHECKED_IN` → "Checked in" (translated when known, humanised otherwise). */
  statusLabel: (value?: string | null) => string;
  relationshipLabel: (value?: string | null) => string;
  languageLabel: (value?: string | null) => string;
  sexLabel: (value?: string | null) => string;
  /** `SOME_CODE` → "Some code" — for values no dictionary knows. */
  humanize: (value?: string | null) => string;
};

const I18nContext = createContext<I18n | null>(null);

function interpolate(template: string, vars?: TranslationVars) {
  if (!vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) => String(vars[key] ?? ''));
}

function parseDate(value?: string | Date | null): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function humanizeCode(value?: string | null): string {
  if (!value) return '';
  const text = String(value).replace(/[_-]+/g, ' ').trim().toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const cache = new Map<SupportedLanguage, Messages>();

export function I18nProvider({ children, fallback = null }: { children: ReactNode; fallback?: ReactNode }) {
  const { language, direction, isRTL } = useLanguage();
  const clinicTimeZone = useClinicTimeZone();
  const [ready, setReady] = useState(() => cache.has('en') && cache.has(language));
  // Bumped whenever a dictionary lands so the memo below re-reads the cache.
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const needed: SupportedLanguage[] = language === 'en' ? ['en'] : ['en', language];
    Promise.all(
      needed.map(async (lang) => {
        if (!cache.has(lang)) cache.set(lang, await loadMessages(lang));
      }),
    )
      .catch(() => {
        // A failed chunk leaves the fallback in place; English always loads with the app.
      })
      .finally(() => {
        if (cancelled) return;
        setReady(cache.has('en'));
        setVersion((n) => n + 1);
      });
    return () => {
      cancelled = true;
    };
  }, [language]);

  const value = useMemo<I18n>(() => {
    const locale = localeForLanguage(language);
    const messages = cache.get(language);
    const en = cache.get('en');
    const lookup = (key: string): string | undefined =>
      (messages as Record<string, string> | undefined)?.[key] ?? (en as Record<string, string> | undefined)?.[key];

    const t: I18n['t'] = (key, vars) => interpolate(lookup(key) ?? key, vars);
    const tz = clinicTimeZone ? { timeZone: clinicTimeZone } : {};

    const fmt = (options: Intl.DateTimeFormatOptions) => (value?: string | Date | null) => {
      const date = parseDate(value);
      return date ? new Intl.DateTimeFormat(locale, { ...tz, ...options }).format(date) : t('common.na');
    };

    const coded = (prefix: string) => (value?: string | null) => {
      if (!value) return t('common.na');
      const key = `${prefix}.${String(value).trim().toLowerCase()}`;
      return lookup(key) ?? humanizeCode(value);
    };

    return {
      language,
      locale,
      direction,
      isRTL,
      t,
      formatDate: fmt({ month: 'short', day: 'numeric', year: 'numeric' }),
      formatDateTime: fmt({ month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }),
      formatTime: fmt({ hour: 'numeric', minute: '2-digit' }),
      formatLongDate: fmt({ weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
      formatMonth: fmt({ month: 'long', year: 'numeric' }),
      formatShortDate: fmt({ weekday: 'short', month: 'short', day: 'numeric' }),
      formatNumber: (n, options) => new Intl.NumberFormat(locale, options).format(n),
      formatCurrency: (n, currency) => {
        const amount = Number(n ?? 0);
        try {
          return new Intl.NumberFormat(locale, { style: 'currency', currency: currency || 'USD' }).format(amount);
        } catch {
          return `${amount.toFixed(2)} ${currency}`;
        }
      },
      statusLabel: coded('status'),
      relationshipLabel: coded('common.relationship'),
      languageLabel: coded('common.language'),
      sexLabel: coded('common.sex'),
      humanize: humanizeCode,
    };
  }, [language, direction, isRTL, clinicTimeZone, ready, version]);

  if (!ready) return <>{fallback}</>;
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n must be used within an I18nProvider.');
  return value;
}
